import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { signInWithGoogle } from '../services/auth.service';
import './LoginPage.css';
import { updateAuth } from '../store/slices/authSlice';
import { userService } from '../services/userService';
import { useDispatch } from 'react-redux';
import {setSwipeStats} from '../store/slices/cacheSlice';
import Spinner from '../components/common/Spinner';
const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;
      // Vérifier si l'utilisateur existe par fbid
      let existingUser = null;
      try {
          existingUser = await userService.getUserByFbid(user.uid);
      } catch (error) {
          if (error.response?.status !== 404) {
              throw error;
          }
      }

      // Si pas trouvé par fbid, vérifier par email
      if (!existingUser && user.email) {
          try {
              existingUser = await userService.getUserByEmail(user.email);
          } catch (error) {
              if (error.response?.status !== 404) {
                  throw error;
              }
          }
      }

      // Si l'utilisateur n'existe pas, renvoyer un message d'erreur
      if (!existingUser) {
        setError('Identifiants invalides');
        setLoading(false);
        return;
      }
      if (existingUser && existingUser.status === "Inactive") {
        dispatch(updateAuth(existingUser));
        navigate('/auth/welcome-policy');
        window.location.reload() 
      } else {
        // Mettre à jour Redux avec les données utilisateur
        dispatch(updateAuth(existingUser));

      
        // Mettre à jour les stats de swipe
        dispatch(setSwipeStats({
          dailySwipes: existingUser.dailySwipes ? existingUser.dailySwipes : 0,
          lastSwipeDate: existingUser.lastSwipeDate ? existingUser.lastSwipeDate : null
        }));
        navigate(existingUser.status === 'PendingDeletion' ? '/settings' : '/home');
        window.location.reload() 

    }


    } catch (error) {
      console.error("Erreur de connexion:", error);
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
          setError("Email ou mot de passe incorrect");
          break;
        case 'auth/invalid-email':
          setError("L'adresse email n'est pas valide");
          break;
        case 'auth/too-many-requests':
          setError("Trop de tentatives échouées. Veuillez réessayer plus tard");
          break;
        default:
          setError("Une erreur est survenue lors de la connexion");
      }
    } finally {
      setLoading(false);
    }
  };

  // Connexion via Google : retrouve ou crée l'utilisateur (via userService.resolveSocialUser)
  // puis redirige comme pour la connexion par email.
  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      const { user, isNew } = await userService.resolveSocialUser(result.user, {
        provider: result.providerId || 'google.com'
      });

      dispatch(updateAuth(user));

      if (isNew || user.status === 'Inactive') {
        navigate('/auth/welcome-policy');
      } else if (user.status === 'PendingDeletion') {
        navigate('/settings');
      } else {
        dispatch(setSwipeStats({
          dailySwipes: user.dailySwipes ? user.dailySwipes : 0,
          lastSwipeDate: user.lastSwipeDate ? user.lastSwipeDate : null
        }));
        navigate('/home');
      }
      window.location.reload();
    } catch (error) {
      console.error('Erreur de connexion Google:', error);

      // L'utilisateur a simplement fermé la popup : pas d'erreur à afficher
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        // rien à faire
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setError('Ce compte existe déjà avec une autre méthode de connexion');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Trop de tentatives échouées. Veuillez réessayer plus tard');
      } else {
        setError('Une erreur est survenue lors de la connexion avec Google');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {loading && <Spinner />}
      <div className="logo-container">
        <img 
          src={process.env.PUBLIC_URL + '/assets/img/fulllogo_transparent_nobuffer.png'} 
          alt="Trakna Logo" 
          className="login-logo"
        />
      </div>
      <button 
        className="close-button" 
        onClick={() => navigate('/')}
        aria-label="Fermer"
      >
        <i className="fas fa-times"></i>
      </button>
      <div className="login-container">
        <h1>Connexion</h1>
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Votre email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Votre mot de passe"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        <div className="alternative-options">
          <button
            className="btn btn-google w-100"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <i className="fab fa-google me-2"></i>
            {t('welcome.continueWithGoogle')}
          </button>

          <div className="signup-link">
            Pas encore de compte ?{' '}
            <Link to="/signup">S'inscrire maintenant</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
